import {Component, computed, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BasicTaDetails,
  SessionDetails,
  TAStatus,
  TaTemplateDetails,
  TaTemplateSummary,
  TaWorkflowType,
  UserService,
  UserType,
} from '../../../api';
import {Button} from 'primeng/button';
import {Card} from "primeng/card";
import {TableModule} from 'primeng/table';
import {FormsModule} from '@angular/forms';
import {HomeTab, toHomeTab, toTAStatusText} from '../../../util/app.types';
import {LocationService} from '../../../services/location.service';
import {TaListBase} from '../ta-list-base.directive';
import {SessionService} from '../../../services/session.service';
import {TabsModule} from 'primeng/tabs';
import {MySubmissionsListComponent} from '../../components/ta-list-tables/my-approvals-list/my-submissions-list.component';
import {MyHistoryListComponent} from '../../components/ta-list-tables/my-history-list/my-history-list.component';
import {DelegateTaListComponent} from '../../components/ta-list-tables/delegate-ta-list/delegate-ta-list.component';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {Tooltip} from 'primeng/tooltip';
import {ActivatedRoute} from '@angular/router';
import {Menu} from 'primeng/menu';
import {MenuItem} from 'primeng/api';
import {TemplateDatasource} from '../../../datasources/template.datasource';
import {MyTemplatesList} from '../../components/ta-list-tables/my-templates-list/my-templates-list';
import {TemplateSummaryCard} from '../../components/template-summary-card/template-summary-card';
import {CreateTaFromTemplateDialog} from '../../components/create-ta-from-template-dialog/create-ta-from-template-dialog';
import {CreateTemplateFromTaDialog} from '../../components/create-template-from-ta-dialog/create-template-from-ta-dialog';
import {TaInfoCardComponent} from '../../components/ta-info-card/ta-info-card.component';
import {CreateTaStepType} from '../../../util/app.types';

@Component({
  selector: 'app-clc-home',
  imports: [
    Button,
    Card,
    TableModule,
    FormsModule,
    TabsModule,
    MySubmissionsListComponent,
    MyHistoryListComponent,
    DelegateTaListComponent,
    ScrollHeightDirective,
    Tooltip,
    Menu,
    MyTemplatesList,
    TemplateSummaryCard,
    CreateTaFromTemplateDialog,
    CreateTemplateFromTaDialog,
    TaInfoCardComponent,
  ],
  templateUrl: './clc-home.html',
  styleUrl: './clc-home.scss'
})
export class ClcHome extends TaListBase {
  tabOption = signal<HomeTab>('inProgress');

  public readonly loading = signal(false);
  private destroyRef = inject(DestroyRef);
  session: SessionDetails | undefined;
  private userService = inject(UserService);
  private activeSubmissions = signal<BasicTaDetails[]>([]);

  private hasOverdueAcquittal = computed(() => {
    const cutoff = new Date(Date.now() - 28 * 24 * 60 * 60 * 1000);
    return this.activeSubmissions().some(ta =>
      ta.status === TAStatus.PendingAcquittal &&
      !!ta.endDate &&
      new Date(ta.endDate) < cutoff
    );
  });
  addButtonDisabled = computed<boolean>(() =>
    !this.permissionService.canCreateTa() || this.hasOverdueAcquittal()
  );
  disabledMessaged = computed<string>(() => {
    if (!this.permissionService.canCreateTa()) {
      return 'Creating TA disabled. Contact Finance for help.';
    }
    if (this.hasOverdueAcquittal()) {
      return 'Has TAs requiring acquittal that are overdue.';
    }
    return '';
  });

  canCreateDelegateTa = computed<boolean>(() => this.permissionService.canCreateDelegateTa());
  // Header Create a TA buttons; booking officers also get one for another traveller. Only the
  // single button carries the icon, per the designs.
  createTaButtons = computed<{label: string, forMe: boolean, testId: string, icon?: string}[]>(() => this.canCreateDelegateTa()
    ? [{label: 'Create TA for me', forMe: true, testId: 'add-ta-button'},
       {label: 'Create TA for another', forMe: false, testId: 'add-delegate-ta-button'}]
    : [{label: 'Create a TA', forMe: true, testId: 'add-ta-button', icon: 'fa-regular fa-location-plus'}]);

  // Hides the right-hand template summary card and TA info card. The cards and their wiring are
  // kept intact so they can be restored by setting this back to true.
  protected readonly showSideCards = false;

  templateDatasource = inject(TemplateDatasource);
  selectedTemplate = signal<TaTemplateSummary | undefined>(undefined);
  // The summary panel's details, once loaded for the selected row.
  selectedTemplateDetails = computed<TaTemplateDetails | undefined>(() => {
    const template = this.templateDatasource.template();
    return !!template && template.id === this.selectedTemplate()?.id ? template : undefined;
  });
  fromTemplateDialogVisible = signal<boolean>(false);
  fromTemplateDialogTemplate = signal<TaTemplateSummary | undefined>(undefined);
  fromTemplateDialogForSelf = signal<boolean>(true);
  createTemplateDialogVisible = signal<boolean>(false);
  templateSourceTa = signal<Pick<BasicTaDetails, 'id' | 'taNumber'> | undefined>(undefined);
  // Header create buttons: default primary colours, squared-off corners.
  protected readonly createButtonTokens = {root: {borderRadius: '6px'}};
  // The dropdown button that opened the menu holds its hover colour until the menu closes.
  protected readonly createButtonOpenTokens = {
    root: {
      borderRadius: '6px',
      primary: {background: '{primary.hover.color}', borderColor: '{primary.hover.color}'},
    },
  };
  createTaMenuOpen = signal<boolean>(false);
  protected readonly createTaMenuTokens = {
    background: '{primary.700}',
    borderColor: '{primary.700}',
    color: '#ffffff',
    item: {color: '#ffffff', focusColor: '#ffffff', focusBackground: '{primary.800}'},
    separator: {borderColor: '{primary.300}'},
  };
  // Flat on purpose: once any item has `items`, p-menu renders every top-level item as an
  // unclickable group label - which swallowed "Create a new, blank TA".
  // Which dropdown button opened the shared menu: booking officers get one for others too.
  createTaMenuForMe = signal<boolean>(true);
  createTaMenuItems = computed<MenuItem[]>(() => [
    {
      id: 'create-ta-menu-blank',
      label: 'Create a new blank TA',
      icon: 'fa-regular fa-location-dot',
      command: () => this.addNewTa(this.createTaMenuForMe()),
    },
    {
      id: 'create-ta-menu-flight-only',
      label: 'Create a new Flights Only TA',
      icon: 'fa-solid fa-plane',
      // Casuals can't be a flight-only traveller, so they can't create one for themselves.
      visible: !(this.createTaMenuForMe() && this.store.user()?.userType === UserType.Casual),
      command: () => this.addNewTa(this.createTaMenuForMe(), TaWorkflowType.FlightOnly),
    },
    ...(this.templateDatasource.hasTemplates() ? [
      {separator: true},
      {
        label: 'Create from a template:',
        icon: 'fa-regular fa-location-plus',
        iconClass: 'text-primary-200',
        labelClass: 'text-xs uppercase tracking-wide text-primary-200',
        styleClass: 'pointer-events-none',
      },
      ...this.templateDatasource.templates().slice(0, 3).map(template => ({
        label: template.name ?? '',
        labelClass: 'italic',
        command: () => this.openCreateTaFromTemplate(template, this.createTaMenuForMe()),
      })),
      {
        label: 'More Templates...',
        labelClass: 'italic',
        command: () => this.onTabChange('templates'),
      },
    ] : []),
  ]);

  constructor(
    protected locationService: LocationService,
    private sessionService: SessionService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    // Subscribed rather than read once from the snapshot: Angular reuses this component when only
    // the query params change, so a nav-link click back to a bare /home must reset the tab too.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => this.applyTabFromUrl(params.get('tab')));
    this.store.resetTaState();
    this.store.updateBreadCrumbData({
      title: 'My Travel Allowances',
      titleIcon: 'fa-regular fa-location-dot fa-lg'
    });

    this.taSubmissionDatasource.resetSource();
    this.locationService.loadLocations();
    this.templateDatasource.loadTemplates();
    this.session = this.sessionService.getSession();
    this.userService.getUserActiveSubmissions().subscribe({
      next: result => { if (result.data) this.activeSubmissions.set(result.data); },
      error: err => console.error('Failed to load active submissions for acquittal check:', err),
    });
  }

  onTabChange(tab: string): void {
    const nextTab = toHomeTab(tab) ?? 'inProgress';
    this.tabOption.set(nextTab);
    // replaceUrl so flicking between tabs does not fill the browser history with home entries.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {tab: nextTab},
      replaceUrl: true,
    });
  }

  addNewTa(forMe: boolean, workflowType: TaWorkflowType = TaWorkflowType.Standard): void {
    // Remember the tab so submitting or deleting the new TA returns here - creating a TA is the one
    // way into the wizard that does not go through viewTaSubmission().
    this.store.updatePreviousPageRoute(this.router.url);
    this.taSubmissionDatasource.createServerTaSubmission(forMe, workflowType);
  }

  selectTemplate(template: TaTemplateSummary): void {
    this.selectedTemplate.set(template);
    if (template.id) {
      this.templateDatasource.loadTemplate(template.id, true);
    }
  }

  openCreateTaFromTemplate(template: TaTemplateSummary | undefined, forSelf: boolean): void {
    if (!template) return;
    this.fromTemplateDialogTemplate.set(template);
    this.fromTemplateDialogForSelf.set(forSelf);
    this.fromTemplateDialogVisible.set(true);
  }

  openCreateTemplate(ta: Pick<BasicTaDetails, 'id' | 'taNumber'> | undefined): void {
    this.templateSourceTa.set(ta);
    this.createTemplateDialogVisible.set(true);
  }

  createTemplate(): void {
    this.templateSourceTa.set(undefined);
    this.createTemplateDialogVisible.set(true);
  }

  editTemplate(template = this.selectedTemplate()): void {
    const id = template?.id;
    if (id) {
      this.router.navigate(['/template', id], {queryParams: {step: CreateTaStepType.TRAVELLERS}});
    }
  }

  // A ?tab= we cannot honour (missing, unknown, or the delegate tab without the permission that
  // renders it) falls back to the default rather than leaving the user on a tab showing nothing.
  private applyTabFromUrl(requested: string | null): void {
    const requestedTab = toHomeTab(requested);
    if (requestedTab && (requestedTab !== 'delegate' || this.canCreateDelegateTa())) {
      this.tabOption.set(requestedTab);
    } else {
      this.tabOption.set('inProgress');
    }
  }

  protected readonly toTAStatusText = toTAStatusText;
}
